import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { translations } from '../../data/translations';
import { Mail, Clock, MapPin, ArrowUpRight } from 'lucide-react';

export const Footer = () => {
  const { t } = useLanguage();

  const navLinks = [
    { path: '/', label: t('home', translations.nav) },
    { path: '/services', label: t('services', translations.nav) },
    { path: '/about', label: t('about', translations.nav) },
    { path: '/contact', label: t('contact', translations.nav) },
  ];

  return (
    <footer className="bg-[#0a0d10] border-t border-gray-800/50" data-testid="footer">
      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">LET'S WORK TOGETHER</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
              {t('title', translations.cta)}
            </h2>
          </div>
          <div className="lg:text-right">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-4 text-xl font-semibold text-blue-400 hover:text-blue-300 transition-colors"
              data-testid="footer-cta"
            >
              <span>{t('button', translations.cta)}</span>
              <div className="w-12 h-12 border border-blue-400/50 group-hover:border-blue-300 group-hover:bg-blue-500/20 flex items-center justify-center transition-all">
                <ArrowUpRight className="w-5 h-5" />
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer Content */}
      <div className="border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">N</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-bold text-lg tracking-tight">NATCAS</span>
                  <span className="text-gray-600 text-[10px] font-medium tracking-[0.2em] uppercase">Group</span>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
                Professional industrial support, logistics operations, and technical services across the Nordic region.
              </p>
            </div>

            {/* Navigation */}
            <div>
              <p className="text-gray-500 text-xs font-mono tracking-wider mb-6">NAVIGATION</p>
              <nav className="space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                    className="block text-gray-400 hover:text-white text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Contact */}
            <div>
              <p className="text-gray-500 text-xs font-mono tracking-wider mb-6">CONTACT</p>
              <div className="space-y-4">
                <a href="mailto:info@natcasgroup.com" className="flex items-center gap-3 text-gray-400 hover:text-white text-sm transition-colors">
                  <Mail className="w-4 h-4 text-blue-500" />
                  info@natcasgroup.com
                </a>
                <div className="flex items-center gap-3 text-gray-500 text-sm">
                  <Clock className="w-4 h-4 text-blue-500" />
                  Mon – Fri: 08:00 – 16:00
                </div>
                <div className="flex items-center gap-3 text-gray-500 text-sm">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  Nordic Region
                </div>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-16 pt-8 border-t border-gray-800/50 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-xs">
              {t('copyright', translations.footer)}
            </p>
            <p className="text-gray-700 text-xs font-mono">
              INDUSTRIAL · LOGISTICS · TECHNICAL
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
